// src/pages/QuizPage/hooks/useSessionQuiz.ts
import { useState, useEffect, useCallback, useRef } from "react";
import { sessionsAPI } from "../../../api";
import { mapRawQuestions } from "../utils/quizLang";
import { quizStorage } from "../utils/quizStorage";
import { shuffleArray, getShuffleSettings } from "../utils/shuffleUtils";
import type { Question, AnswerResult } from "../utils/quizTypes";

const MAX_WRONG_NAZORAT = 3;
const EXAM_SECONDS = 25 * 60; // 25 daqiqa
const AUTO_ADVANCE_DELAY = 600; // ms — to'g'ri javobdan keyin keyingi savol
const AUTO_FINISH_DELAY = 1000; // ms — hammasi javob berilgach finish

type UiLang = "oz" | "uz" | "ru";

interface UseSessionQuizOptions {
  mode: "topic" | "ticket" | "tickets" | "nazorat" | "exam" | "random";
  id?: string | number | null; // topicId | ticketId | random uchun count
  lang: UiLang;
}

export function useSessionQuiz({ mode, id, lang }: UseSessionQuizOptions) {
  const [rawQs, setRawQs] = useState<any[]>([]);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [current, setCurrent] = useState(0);
  const [answered, setAnswered] = useState<Record<number, number>>({});
  const [answerMeta, setAnswerMeta] = useState<Record<number, AnswerResult>>(
    {},
  );
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [finished, setFinished] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [timeSpent, setTimeSpent] = useState(0);

  const finishCalledRef = useRef(false);
  // State (answered) asinxron yangilanadi — tez-tez bosishda bitta savolga
  // ikki marta javob hisoblanib ketmasligi uchun sinxron ref bilan himoya.
  const answeredQuestionsRef = useRef<Set<number>>(new Set());

  const isTicketMode = mode === "ticket" || mode === "tickets";
  const isNazoratMode = mode === "nazorat" || mode === "exam";
  const hasTimer = isTicketMode || isNazoratMode;

  // ─── INIT: cache yoki yangi session ────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      finishCalledRef.current = false;
      setFinished(false);
      setError(null);

      // 1. Cache tekshirish
      const cached = quizStorage.load(mode, id);
      if (cached) {
        setRawQs(cached.rawQs);
        setAnswered(cached.answered || {});
        setAnswerMeta(cached.answerMeta || {});
        setCurrent(cached.current || 0);
        setTimeLeft(cached.timeLeft || 0);
        setTimeSpent(cached.timeSpent || 0);
        setSessionId(cached.sessionId || null);
        setLoading(false);
        return;
      }

      // 2. Reset
      answeredQuestionsRef.current = new Set();
      setRawQs([]);
      setQuestions([]);
      setAnswered({});
      setAnswerMeta({});
      setCurrent(0);
      setTimeSpent(0);
      setSessionId(null);
      setLoading(true);

      // 3. Backend mode mapping
      const backendMode = mode === "nazorat" ? "exam" : mode;

      try {
        const payload: any = { mode: backendMode };
        if (mode === "topic" && id) payload.topicId = Number(id);
        if (isTicketMode && id) payload.ticketId = Number(id);
        if (mode === "random" && id) payload.count = Number(id);

        const res = await sessionsAPI.start(payload);
        if (cancelled) return;

        const raw = res.data?.questions || [];
        const sId = res.data?.sessionId || res.data?.id || null;

        if (raw.length > 0) {
          const { shuffleQuestions } = getShuffleSettings();
          setRawQs(shuffleQuestions ? shuffleArray(raw) : raw);
          setSessionId(sId);
          if (hasTimer) setTimeLeft(EXAM_SECONDS);
        }
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.response?.data?.message || "Test boshlanmadi");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    init();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, id]);

  // ─── rawQs → questions mapping (lang change safe) ──────────────────
  useEffect(() => {
    if (!rawQs.length) return;
    setQuestions((prev) => mapRawQuestions(rawQs, lang, answerMeta, prev));
  }, [rawQs, lang, answerMeta]);

  // ─── Timer ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (finished || !questions.length) return;
    const interval = setInterval(() => {
      setTimeSpent((p) => p + 1);
      if (hasTimer) setTimeLeft((p) => (p > 0 ? p - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [finished, questions.length, hasTimer]);

  // ─── Cache save ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!questions.length || finished) return;
    quizStorage.save(mode, id, {
      rawQs,
      answered,
      answerMeta,
      current,
      lives: 3, // sessionQuiz uchun ishlatilmaydi, lekin tip uchun
      timeLeft,
      timeSpent,
      sessionId,
    });
  }, [
    answered,
    current,
    mode,
    id,
    rawQs,
    answerMeta,
    timeLeft,
    timeSpent,
    sessionId,
    questions.length,
    finished,
  ]);

  // ─── Hisoblar ──────────────────────────────────────────────────────
  const correctCount = Object.keys(answered).filter(
    (k) => questions[Number(k)]?.correct_answer_index === answered[Number(k)],
  ).length;

  const wrongCount = Object.keys(answered).filter(
    (k) => questions[Number(k)]?.correct_answer_index !== answered[Number(k)],
  ).length;

  // ─── FINISH ─────────────────────────────────────────────────────────
  // Session javoblarni saqlamaydi — finishda barcha savollar bo'yicha
  // bulk answers array yuboriladi (javob berilmaganlari answerIndex: null).
  const finish = useCallback(async () => {
    if (finishCalledRef.current) return;
    finishCalledRef.current = true;

    quizStorage.clear(mode, id);
    quizStorage.clearMarafonLeftover();

    try {
      if (sessionId) {
        const answersPayload = questions.map((q, idx) => ({
          questionId: q.id,
          answerIndex: answered[idx] ?? null,
        }));
        await sessionsAPI.finish(sessionId, timeSpent, answersPayload);
      }
    } catch (e) {
      // Silent fail — frontend hali ham natijani ko'rsatadi
    } finally {
      setFinished(true);
    }
  }, [sessionId, timeSpent, mode, id, questions, answered]);

  // ─── ANSWER ─────────────────────────────────────────────────────────
  // Endi networkka bormaydi — correct_answer_index sessiya boshida
  // savol bilan birga kelgan, shu asosda local tekshiriladi.
  const handleAnswer = useCallback(
    (orderIndex: number) => {
      if (answeredQuestionsRef.current.has(current) || finished) return;
      const q = questions[current];
      if (!q) return;
      answeredQuestionsRef.current.add(current);

      const isCorrect = q.correct_answer_index === orderIndex;

      const newAnswered = { ...answered, [current]: orderIndex };
      setAnswered(newAnswered);
      setAnswerMeta((prev) => ({
        ...prev,
        [q.id]: {
          selectedIndex: orderIndex,
          isCorrect,
          correctAnswerIndex: q.correct_answer_index,
        },
      }));

      // Auto-advance: to'g'ri javob → 600ms keyin keyingi savol (oxirgi emas)
      if (isCorrect && current < questions.length - 1) {
        setTimeout(() => {
          setCurrent((p) => Math.min(questions.length - 1, p + 1));
        }, AUTO_ADVANCE_DELAY);
      }

      // All answered → auto-finish
      if (Object.keys(newAnswered).length === questions.length) {
        setTimeout(() => finish(), AUTO_FINISH_DELAY);
      }
    },
    [answered, current, finished, questions, finish],
  );

  // ─── Timer expire → finish ─────────────────────────────────────────
  useEffect(() => {
    if (loading || finished || timeSpent === 0) return;
    if (hasTimer && timeLeft === 0 && questions.length) {
      finish();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft, finished, hasTimer, questions.length, timeSpent, loading]);

  // ─── Nazorat: 3 xato → finish ──────────────────────────────────────
  useEffect(() => {
    if (finished || !sessionId || !isNazoratMode) return;
    if (wrongCount >= MAX_WRONG_NAZORAT) finish();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wrongCount, finished, isNazoratMode, sessionId]);

  // ─── Reset ─────────────────────────────────────────────────────────
  const reset = useCallback(() => {
    quizStorage.clear(mode, id);
    finishCalledRef.current = false;
    answeredQuestionsRef.current = new Set();
    // Re-trigger init useEffect orqali (mode, id o'zgarmaydi, lekin reset force)
    setRawQs([]);
    setQuestions([]);
    setAnswered({});
    setAnswerMeta({});
    setCurrent(0);
    setTimeSpent(0);
    setTimeLeft(0);
    setSessionId(null);
    setFinished(false);
    setError(null);
    setLoading(true);

    // Manual re-init (mode/id o'zgarmagani uchun useEffect re-trigger qilmaydi)
    const backendMode = mode === "nazorat" ? "exam" : mode;
    const payload: any = { mode: backendMode };
    if (mode === "topic" && id) payload.topicId = Number(id);
    if (isTicketMode && id) payload.ticketId = Number(id);
    if (mode === "random" && id) payload.count = Number(id);

    sessionsAPI
      .start(payload)
      .then((res) => {
        const raw = res.data?.questions || [];
        const sId = res.data?.sessionId || res.data?.id || null;
        if (raw.length > 0) {
          const { shuffleQuestions } = getShuffleSettings();
          setRawQs(shuffleQuestions ? shuffleArray(raw) : raw);
          setSessionId(sId);
          if (hasTimer) setTimeLeft(EXAM_SECONDS);
        }
      })
      .catch((e: any) => {
        setError(e?.response?.data?.message || "Test qayta boshlanmadi");
      })
      .finally(() => setLoading(false));
  }, [mode, id, isTicketMode, hasTimer]);

  return {
    // State
    questions,
    current,
    answered,
    answerMeta,
    sessionId,
    loading,
    error,
    finished,
    timeLeft,
    timeSpent,
    correctCount,
    wrongCount,
    // Meta flags
    isTicketMode,
    isNazoratMode,
    hasTimer,
    // Actions
    handleAnswer,
    setCurrent,
    finish,
    reset,
  };
}
